import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { io, Socket } from "socket.io-client";
import { auth } from "../firebase";

// Callback type
type Callback = (fullDocument: any) => void;

// EventMap tracks subscriptions: event -> id -> callbacks
type EventMap = {
  [event: string]: {
    [id: string]: Set<Callback>;
  };
};

interface SocketContextType {
  socket: Socket | null;
  subscribe: (event: string, id: string, callback: Callback) => void;
  unsubscribe: (event: string, id: string, callback: Callback) => void;
  disconnectSocket: () => void; // Optional for cleanup
}

// No default implementation — throws if used outside provider
const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("❌ useSocket must be used within a <SocketProvider>");
  }
  return context;
};

export const SocketProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const eventMapRef = useRef<EventMap>({});

  const connectSocket = async () => {
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : null;
    if (!token) return;

    const socketInstance = io(
      process.env.REACT_APP_API_URL || "http://localhost:4000",
      {
        withCredentials: true,
        auth: { token },
      },
    );

    socketInstance.on("connect", () => {
      console.log("✅ Socket connected");
    });

    socketInstance.onAny((event, data) => {
      const { id, fullDocument } = data || {};
      const callbacksById = eventMapRef.current?.[event];
      if (!callbacksById || !fullDocument) return;

      console.log(
        `🔔 Received event: ${event} for id: ${id}, invoking callbacks...`,
      );

      Object.entries(callbacksById).forEach(([subscribedId, callbacks]) => {
        if (subscribedId === "*" || subscribedId === id) {
          callbacks.forEach((cb) => cb(fullDocument));
        }
      });
    });

    setSocket(socketInstance);
    return socketInstance;
  };

  const disconnectSocket = () => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
  };

  const subscribe = (event: string, id: string, callback: Callback) => {
    if (!socket) {
      console.warn(
        `⚠️ Tried to subscribe to ${event}:${id} before socket was ready.`,
      );
      return;
    }

    if (!eventMapRef.current[event]) {
      eventMapRef.current[event] = {};
    }
    if (!eventMapRef.current[event][id]) {
      eventMapRef.current[event][id] = new Set();
    }
    eventMapRef.current[event][id].add(callback);
  };

  const unsubscribe = (event: string, id: string, callback: Callback) => {
    const callbacks = eventMapRef.current[event]?.[id];
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        delete eventMapRef.current[event][id];
      }
    }
  };

  useEffect(() => {
    let currentSocket: Socket | null = null;

    const unsubscribeAuth = auth.onIdTokenChanged(async (user) => {
      disconnectSocket();
      if (user) {
        currentSocket = (await connectSocket()) || null;
      }
    });

    if (auth.currentUser) {
      connectSocket().then((sock) => {
        currentSocket = sock || null;
      });
    }

    return () => {
      unsubscribeAuth();
      if (currentSocket) {
        currentSocket.disconnect();
        setSocket(null);
      }
    };
  }, []);

  return (
    <SocketContext.Provider
      value={{ socket, subscribe, unsubscribe, disconnectSocket }}
    >
      {children}
    </SocketContext.Provider>
  );
};
